import { useState, type MouseEvent, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircleCheck } from "lucide-react";
import { useTranslate } from "@/lib/i18n";
import type { DeliveryTabGridEntry } from "@/lib/api/dispatchTypes";
import type { InvoiceHandoff } from "@/modules/pos/pages/InvoicePage";
import { useMoney } from "@/shared/ui";
import { paymentLine, waitingTotal, type WithDriverRow } from "@/modules/pos/lib/deliveryWaiting";
import { DeliveryAddressLine } from "./DeliveryAddressLine";
import { ItemsToggle, LaneAge, OrderNumber, toggleIn } from "./DeliveryLane";
import { WaitingOrderLines } from "./WaitingOrderLines";

/**
 * «مع المندوب» — the Delivery tab's second lane (G24b M1; a lane of cards in
 * the 2026-10-26 redesign): every own bag a driver holds past the offer —
 * accepted, on the way, at the door, or reported a problem — OPEN or CLOSED.
 * Each card: the number (opens the bill), who, how long since it opened, the
 * driver's chip (`floorDispatch.chip`), where, the total and how it is paid,
 * and the bag's own dispatch controls (`floorDispatch.action(bill, "paper")`:
 * hand over, follow up, the problem report, the tracking share). A problem
 * card gets a red ring.
 */
export function WithDriverTable({
  rows,
  canOpen,
  chip,
  action,
  now,
  problem,
}: {
  rows: readonly WithDriverRow[];
  canOpen: boolean;
  /** The bag's driver chip (`floorDispatch.chip`). */
  chip: (bill: DeliveryTabGridEntry) => ReactNode;
  /** The bag's dispatch controls (`floorDispatch.action(bill, "paper")`). */
  action: (bill: DeliveryTabGridEntry) => ReactNode;
  /** The lane's clock (ms), for each card's age. */
  now: number;
  /** Whether the bag's driver reported a problem: the card's red ring. */
  problem?: (row: WithDriverRow) => boolean;
}) {
  const t = useTranslate();
  const money = useMoney();
  const navigate = useNavigate();
  const { locale = "ar" } = useParams();
  const title = t((m) => m.dispatch.withDriverTitle);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  /* 2026-10-20 — the number and the name open the bill too. */
  const openBill = (event: MouseEvent, bill: DeliveryTabGridEntry) => {
    event.stopPropagation();
    if ((event.target as HTMLElement).closest("a")) return;
    navigate(`/${locale}/pos/invoices/${bill.invoiceId}`, {
      state: bill.status === "CLOSED" ? ({ settle: true } satisfies InvoiceHandoff) : null,
    });
  };

  return (
    <section data-ui="dl-lane" data-lane="driver" data-testid="with-driver-card" aria-label={title} className="flex min-w-0 flex-col">
      <header data-ui="dl-lane-head" className="flex items-center">
        <span aria-hidden="true" data-ui="dl-dot" data-tone="amber" />
        <h2 data-ui="dl-lane-title">{title}</h2>
        <span data-ui="dl-count" data-testid="with-driver-count" className="tabular-nums">
          {rows.length}
        </span>
      </header>
      <p data-ui="dl-lane-hint">{t((m) => m.dispatch.laneWithHint)}</p>
      {rows.length === 0 ? (
        <p data-ui="dl-empty" data-testid="with-driver-empty" className="flex items-center justify-center">
          <CircleCheck aria-hidden="true" size={16} strokeWidth={2} />
          <span>{t((m) => m.dispatch.laneWithEmpty)}</span>
        </p>
      ) : (
        <div data-ui="dl-cards" role="list" aria-label={title} className="flex flex-col">
          {rows.map((row) => {
            const { bill } = row;
            const seq = bill.dailySequence;
            const pay = paymentLine(bill);
            const line = chip(bill);
            return (
              <article
                key={bill.invoiceId}
                role="listitem"
                data-ui="dl-card"
                data-alert={problem?.(row) ? "" : undefined}
                data-testid={`with-driver-row-${seq}`}
                className="flex flex-col"
              >
                <div data-ui="dl-row" className="flex items-center">
                  <OrderNumber seq={seq} canOpen={canOpen} testId={`with-driver-number-${seq}`} onOpen={(event) => openBill(event, bill)} />
                  <span
                    data-ui="dl-name"
                    data-openable={canOpen ? "" : undefined}
                    data-testid={`with-driver-to-${seq}`}
                    onClick={canOpen ? (event) => openBill(event, bill) : undefined}
                    className="min-w-0 truncate"
                  >
                    <bdi>{bill.customerName?.trim() || t((m) => m.dispatch.waitingNoName)}</bdi>
                  </span>
                  <span className="flex-1" />
                  <LaneAge since={bill.openedAt} now={now} />
                </div>
                {line ? (
                  <span className="flex min-w-0" data-testid={`with-driver-chip-${seq}`}>
                    {line}
                  </span>
                ) : null}
                <DeliveryAddressLine bill={bill} testId={`with-driver-address-${seq}`} />
                <div data-ui="dl-row" className="flex items-center">
                  <span data-ui="dl-money" className="tabular-nums">{money(waitingTotal(bill))}</span>
                  {pay ? (
                    <span data-ui="dl-pay">
                      {pay === "cod" ? t((m) => m.dispatch.payCashOnDelivery) : t((m) => m.dispatch.payPaid)}
                    </span>
                  ) : null}
                  <span className="flex-1" />
                  <ItemsToggle
                    seq={seq}
                    count={bill.plates?.chargeable ?? 0}
                    open={expanded.has(bill.invoiceId)}
                    onToggle={() => setExpanded((current) => toggleIn(current, bill.invoiceId))}
                    testId={`with-driver-items-${seq}`}
                  />
                </div>
                {expanded.has(bill.invoiceId) ? (
                  <div data-ui="dl-lines">
                    <WaitingOrderLines invoiceId={bill.invoiceId} testId={`with-driver-lines-${seq}`} />
                  </div>
                ) : null}
                <div data-ui="dl-actions" data-testid={`with-driver-actions-${seq}`} className="flex flex-wrap items-center">
                  {action(bill)}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
